import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, RefreshCw, XCircle, Clock, Scan, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as notificationApi from '@/services/notificationApi';
import * as authApi from '@/services/authApi';

const IncomingConsignments = () => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [selectedBundles, setSelectedBundles] = useState({ bundles: [], document_barcode: '' });
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const scanInputRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchConsignments();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      setupWebSocket();
    }
  }, [user]);

  useEffect(() => {
    // Focus scan input when modal opens
    if (showScanModal && scanInputRef.current) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [showScanModal]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchConsignments = async () => {
    setIsLoading(true);
    try {
      const data = await notificationApi.getIncomingConsignments();
      setConsignments(data.consignments || []);
    } catch (error) {
      console.error('Error fetching consignments:', error);
      toast.error('Failed to load consignments');
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    if (!user) return;
    
    // Notification sound
    const notificationSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telehmg1/NjIQAsicHQpJ4cLouu8JHCqWUEADqk0dNdEDxgktzS12xwLxJPl8/Mxc/I2JYWBFltsrHd1dvgixwBRWWQoOjv0uKQMwAtXm+X1e3d0qx0GgM5X4ed5vHZ3YElBy1ler3k6+HYkjkLIVN3m9vu6tyYRBEeTnGV3O7n26FPGhZIZ4m94+zg2plOGQ9CYoiy4uzc2ppSHQ0+YIWw4uva2ZtTHgs6Xoau4uvZ2JtUHwo4XYSs4uvY15xVIAg2XIKq4erX1p1WIQczW4Go4enW1Z5XIwYxWoCn4OnV1J9YJAQvWX+l3+jU05xZJQMuWH+k3+fT0p1aJgItV36j3ubS0Z5bJgEsV32i3eXR0J9cJwArVnyh3OTQz6BdKAApVXuf2+PPzqFeKAAoVHqe2uLOzaJfKgAnU3md2eHNzKNgKgAmUnib2ODMy6RhKwAlUXea19/LyqViLAAkUHaZ1t7KyaZjLQAjT3WY1d3JyKdkLgAiTnSX1NzIx6hlLwAhTXOW09vHxqlmMAAhTHKV0trGxapoMQAgS3GU0dnFxKtpMgAfSnCTz9jEw6xqMwAeSm+Sztf');
    
    wsRef.current = notificationApi.createNotificationWebSocket(
      user.user_id,
      (data) => {
        if (data.type === 'new_notification') {
          // Play sound
          notificationSound.play().catch(() => {});
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(data.notification.title, {
              body: data.notification.message,
              icon: '/favicon.ico'
            });
          }
          
          // Refresh consignments if relevant notification
          if (['consignment_ready', 'consignment_shipped'].includes(data.notification.type)) {
            fetchConsignments();
          }
          
          toast.info(data.notification.title);
        }
      },
      (error) => {
        console.error('WebSocket error:', error);
      }
    );
  };

  const handleRequestConsignment = async (entryId) => {
    setActionInProgress(entryId);
    try {
      await notificationApi.requestConsignment(entryId);
      toast.success('Consignment requested successfully');
      fetchConsignments();
    } catch (error) {
      console.error('Error requesting consignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to request consignment');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectConsignment = async (entryId) => {
    if (!window.confirm('Are you sure you want to reject this consignment? This will notify Inward Documentation to correct the section.')) {
      return;
    }
    setActionInProgress(entryId);
    try {
      await notificationApi.rejectConsignment(entryId);
      toast.success('Consignment rejected. Inward Documentation has been notified.');
      fetchConsignments();
    } catch (error) {
      console.error('Error rejecting consignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject consignment');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleScanToConfirmReceipt = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;
    
    setIsScanning(true);
    try {
      const result = await notificationApi.confirmReceiptByScan(scanInput.trim());
      toast.success(`Receipt confirmed for ${result.consignment.document_barcode}`);
      setShowScanModal(false);
      setScanInput('');
      fetchConsignments();
    } catch (error) {
      console.error('Error confirming receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm receipt');
      // Keep modal open and select input for retry
      if (scanInputRef.current) {
        scanInputRef.current.select();
      }
    } finally {
      setIsScanning(false);
    }
  };

  const handleViewBundles = async (entryId) => {
    try {
      const data = await notificationApi.getConsignmentBundles(entryId);
      setSelectedBundles(data);
      setShowBundleModal(true);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundles');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6" />
              <h1 className="text-xl font-bold font-display">Incoming Consignments</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowScanModal(true)} 
              className="bg-green-600 hover:bg-green-700 text-white h-10"
              data-testid="scan-receipt-btn"
            >
              <Scan className="w-4 h-4 mr-2" />
              Scan to Confirm Receipt
            </Button>
            <Button onClick={fetchConsignments} variant="secondary" className="h-10">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-6 font-display">
            Consignments for Your Section
          </h2>
          
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
            </div>
          ) : consignments.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No incoming consignments</p>
            </div>
          ) : (
            <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                      <TableHead className="font-semibold text-[#1A1F2E]">Document Barcode</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Section</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Vendor</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Vendor Doc No</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Qty</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Value</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Bundles</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Confirmed At</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consignments.map((consignment) => (
                      <TableRow key={consignment.entry_id} className="hover:bg-[#FFFBF5]">
                        <TableCell className="font-medium text-[#1A1F2E]">
                          {consignment.document_barcode}
                        </TableCell>
                        <TableCell>{consignment.section_name}</TableCell>
                        <TableCell>{consignment.vendor_name || '-'}</TableCell>
                        <TableCell>{consignment.vendor_doc_no || '-'}</TableCell>
                        <TableCell>{consignment.qty || '-'}</TableCell>
                        <TableCell>{consignment.value ? `₹${consignment.value}` : '-'}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleViewBundles(consignment.entry_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            data-testid={`view-bundles-${consignment.entry_id}`}
                          >
                            {consignment.bundle_count || 0}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(consignment.documentation_confirmed_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          {consignment.status === 'ready' ? (
                            <div className="flex flex-col gap-2 items-center">
                              <Button
                                onClick={() => handleRequestConsignment(consignment.entry_id)}
                                size="sm"
                                disabled={actionInProgress === consignment.entry_id}
                                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white w-40"
                                data-testid={`request-consignment-${consignment.entry_id}`}
                              >
                                {actionInProgress === consignment.entry_id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Request Consignment'
                                )}
                              </Button>
                              <Button
                                onClick={() => handleRejectConsignment(consignment.entry_id)}
                                size="sm"
                                variant="outline"
                                disabled={actionInProgress === consignment.entry_id}
                                className="text-red-600 border-red-300 hover:bg-red-50 w-40"
                                data-testid={`reject-consignment-${consignment.entry_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Wrong Section
                              </Button>
                            </div>
                          ) : consignment.status === 'requested' ? (
                            <div className="flex flex-col gap-2 items-center">
                              <Button
                                size="sm"
                                disabled={true}
                                className="bg-gray-400 text-white cursor-not-allowed w-40"
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Requested
                              </Button>
                              <span className="text-xs text-[#5A5F6E]">Waiting for shipment</span>
                            </div>
                          ) : consignment.status === 'shipped' ? (
                            <div className="flex flex-col gap-2 items-center">
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Truck className="w-4 h-4" />
                                Shipped - Scan to Receive
                              </span>
                              <span className="text-xs text-[#5A5F6E]">Use "Scan to Confirm Receipt" button</span>
                            </div>
                          ) : consignment.status === 'received' ? (
                            <div className="flex flex-col gap-2 items-center">
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="w-4 h-4" />
                                Receipt Confirmed
                              </span>
                              <Button
                                onClick={() => navigate(`/sections/${consignment.section_name.toLowerCase().replace(/\s+/g, '-')}/bundle-receipt`)}
                                size="sm"
                                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                              >
                                <Package className="w-4 h-4 mr-1" />
                                Verify Bundles
                              </Button>
                            </div>
                          ) : (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Rejected
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Scan to Confirm Receipt Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5" />
              Scan Document Barcode
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleScanToConfirmReceipt} className="py-4">
            <Label className="text-sm text-[#5A5F6E]">
              Scan or enter the document barcode to confirm receipt of goods
            </Label>
            <Input
              ref={scanInputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan document barcode..."
              className="mt-2"
              disabled={isScanning}
              autoFocus
              data-testid="scan-document-input"
            />
            <div className="flex gap-3 mt-4">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowScanModal(false);
                  setScanInput('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={isScanning || !scanInput.trim()}
              >
                {isScanning ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirm Receipt
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bundle Modal */}
      <Dialog open={showBundleModal} onOpenChange={setShowBundleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bundle QR Codes - {selectedBundles.document_barcode}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedBundles.bundles && selectedBundles.bundles.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {selectedBundles.bundles.map((bundle, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-[#F5F1E8] rounded-lg text-sm font-mono"
                  >
                    {bundle}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#5A5F6E]">No bundles found</p>
            )}
            <p className="mt-4 text-sm text-[#5A5F6E]">
              Total Bundles: <strong>{selectedBundles.bundles?.length || 0}</strong>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomingConsignments;
